import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import matchService from "../../services/api/matchService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import StatusBadge from "../ui/StatusBadge";
import PendingChallenges from "./PendingChallenges";
import { eid } from "../../utils/entityId";
import "../../styles/features/matches.css";
import { eventTimeZone, formatEventTimeForViewer } from "../../utils/eventTime";

const MatchManagement = ({ token, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [currentUser, setCurrentUser] = useState(user || null);

  // Filters state — restore active tab when navigating back from a match detail
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    viewMode: "grid",
    role: location.state?.restoreTab || "participant"
  });

  // Scroll-pagination: show cards progressively
  const [visibleCount, setVisibleCount] = useState(12);
  const gridRef = useRef(null);

  // Debug logging

  // Set auth token and get user data
  useEffect(() => {
    if (token) {
      matchService.setAuthToken(token);
      // Store token for userService interceptors
      localStorage.setItem("token", token);
    }

    // Get user data from localStorage if not provided via props
    if (!currentUser) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setCurrentUser(userData);
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          setError("Authentication error. Please log in again.");
          navigate("/login");
          return;
        }
      } else {
        setError("No user data found. Please log in.");
        navigate("/login");
        return;
      }
    } else {
    }
  }, [token, currentUser, navigate]);

  // Load user's matches
  useEffect(() => {
    if (currentUser) {
      loadMyMatches();
    }
  }, [currentUser, filters.role]);

  const loadMyMatches = async () => {
    if (!currentUser || !currentUser.id) {
      setError("User information not available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");


      // Get matches filtered by role
      const queryParams = { limit: 50 };
      if (filters.role === "participant") {
        // user_id triggers a team-aware join: includes both direct-user matches and
        // any team matches where the user is a member/creator of the team.
        queryParams.user_id = currentUser.id;
      } else if (filters.role === "organizer") {
        queryParams.created_by = currentUser.id;
      }
      
      const response = await matchService.getAllMatches(queryParams);


      if (response.success) {
        const matchData = response.data.matches || response.data || [];
        setMatches(matchData);
      } else {
        setError(response.message || "Failed to load matches");
      }
    } catch (error) {
      console.error("Error loading matches:", error);
      // On error, show empty state instead of blocking error UI
      setMatches([]);
      setError("");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMatch = async () => {
    if (!matchToDelete) return;

    try {
      const response = await matchService.deleteMatch(matchToDelete.id);
      if (response.success) {
        setMatches(matches.filter(match => match.id !== matchToDelete.id));
        setShowDeleteModal(false);
        setMatchToDelete(null);
      } else {
        setError(response.message || "Failed to delete match");
      }
    } catch (error) {
      console.error("Error deleting match:", error);
      setError("Failed to delete match. Please try again.");
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      viewMode: "grid",
      role: "participant"
    });
  };

  // Format date
  // At the venue's local time (utils/eventTime)
  const formatDate = (dateString, timeZone) => {
    if (!dateString) return 'Not scheduled';
    return formatEventTimeForViewer(dateString, timeZone, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter matches
  const filteredMatches = matches.filter(match => {
    const matchesSearch = filters.search === "" || 
      (match.match_name && match.match_name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (match.sport_name && match.sport_name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (match.participant_1_name && match.participant_1_name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (match.participant_2_name && match.participant_2_name.toLowerCase().includes(filters.search.toLowerCase()));
    
    const matchesStatus = filters.status === "" || match.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  // Reset visible cards whenever filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setVisibleCount(12); }, [filters.search, filters.status, filters.role, filters.viewMode]);

  // Match card — compact gaming style matching /matches page
  const MatchCard = ({ match, index }) => {
    if (!match) return null;

    const getScore = (key) => {
      const sd = match.scoreData || match.score_data;
      if (!sd) return 0;
      const raw = sd[key];
      if (raw === undefined || raw === null) return 0;
      return typeof raw === 'object' ? (raw.total ?? raw.score ?? 0) : raw;
    };

    const isCreator = currentUser && (match.created_by === currentUser.id || match.createdBy === currentUser.id);

    return (
      <div
        className={`match-card-gaming delay-${(index % 5) + 1}`}
        onClick={() => navigate(`/matches/${eid(match)}`, { state: { from: '/matches/manage', tab: filters.role } })}
        style={{ cursor: 'pointer' }}
      >
        <div className="match-header-gaming">
          <div className="match-title-section">
            <div className="match-number-gaming">
              <span className="match-hash">#</span>
              <span className="match-id">{match.id}</span>
            </div>
            <h6 className="match-title-gaming">
              {(() => {
                const stored = match.matchName || match.match_name || `${match.sport_name || 'Sport'} Match`;
                const p1 = match.participant1Name || match.participant_1_name;
                const p2 = match.participant2Name || match.participant_2_name;
                if (p1 && p2 && /Participant \d+/i.test(stored)) {
                  const tn = match.tournamentName || match.tournament_name;
                  return tn ? `${tn}: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
                }
                return stored;
              })()}
            </h6>
          </div>
          <div className="match-status-gaming">
            <StatusBadge status={match.status} kind="match" />
          </div>
        </div>

        <div className="match-participants-gaming">
          <div className="participant-gaming participant-1">
            <div className="participant-header">
              <h6 className="participant-name">
                {match.participant1Name || match.participant_1_name || 'TBD'}
              </h6>
              <span className="participant-type">
                {(match.participant1Type || match.participant_1_type) === 'user' ? '👤 Player' : '👥 Team'}
              </span>
            </div>
            <div className="participant-score-gaming">{getScore('participant1')}</div>
          </div>
          <div className="vs-separator-gaming">
            <div className="vs-circle-gaming"><span>VS</span></div>
          </div>
          <div className="participant-gaming participant-2">
            <div className="participant-header">
              <h6 className="participant-name">
                {match.participant2Name || match.participant_2_name || 'TBD'}
              </h6>
              <span className="participant-type">
                {(match.participant2Type || match.participant_2_type) === 'user' ? '👤 Player' : '👥 Team'}
              </span>
            </div>
            <div className="participant-score-gaming">{getScore('participant2')}</div>
          </div>
        </div>

        <div className="match-details-gaming">
          <div className="detail-item">
            <span className="detail-icon">🏆</span>
            <span className="detail-label">Sport:</span>
            <span className="detail-value">{match.sport_name || 'Unknown'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-icon">📅</span>
            <span className="detail-label">Date:</span>
            <span className="detail-value">{formatDate(match.scheduledAt || match.scheduled_at, eventTimeZone(match))}</span>
          </div>
          {match.venue && (
            <div className="detail-item">
              <span className="detail-icon">📍</span>
              <span className="detail-label">Venue:</span>
              <span className="detail-value">{match.venue}</span>
            </div>
          )}
        </div>

        <div className="match-actions-gaming" onClick={(e) => e.stopPropagation()}>
          {filters.role === 'organizer' && isCreator && (
            <>
              <FloatingButton
                variant="warning"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/matches/${eid(match)}`, { state: { from: '/matches/manage', tab: filters.role, openEdit: true } });
                }}
                icon="✏️"
              >
                <span className="btn-text">Edit</span>
              </FloatingButton>
              {(match.status === 'scheduled' || match.status === 'created') && (
                <FloatingButton
                  variant="success"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      match.sport_name?.toLowerCase() === 'badminton'
                        ? `/matches/${match.id}/score`
                        : `/matches/${match.id}/live`,
                      { state: { from: '/matches/manage', tab: filters.role } }
                    );
                  }}
                  icon="▶️"
                >
                  <span className="btn-text">Start</span>
                </FloatingButton>
              )}
              <FloatingButton
                variant="danger"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMatchToDelete(match);
                  setShowDeleteModal(true);
                }}
                icon="🗑️"
              >
                <span className="btn-text">Delete</span>
              </FloatingButton>
            </>
          )}
        </div>
      </div>
    );
  };

  // List row component — compact single-row layout
  const MatchListRow = ({ match, index }) => {
    if (!match) return null;

    const scoreData = match.scoreData || match.score_data;
    const getScore = (key) => {
      if (!scoreData) return 0;
      const raw = scoreData[key];
      if (raw === undefined || raw === null) return 0;
      return typeof raw === 'object' ? (raw.total ?? raw.score ?? 0) : raw;
    };

    const p1Name = match.participant1Name || match.participant_1_name || 'Team 1';
    const p2Name = match.participant2Name || match.participant_2_name || 'Team 2';
    const hostName = (match.creatorFirstName || match.creator_first_name)
      ? `${match.creatorFirstName || match.creator_first_name} ${match.creatorLastName || match.creator_last_name}`
      : 'N/A';
    const isCreator = currentUser && (match.createdBy === currentUser.id || match.created_by === currentUser.id);

    return (
      <div
        className={`match-list-row delay-${(index % 5) + 1}`}
        onClick={() => navigate(`/matches/${eid(match)}`, { state: { from: '/matches/manage', tab: filters.role } })}
      >
        {/* Status */}
        <StatusBadge status={match.status} kind="match" />

        {/* Match name */}
        <div className="list-match-name">
          <span className="list-match-id">#{match.id}</span>
          <span className="list-match-title">{(() => {
            const stored = match.matchName || match.match_name || `Match #${match.id}`;
            const p1 = match.participant1Name || match.participant_1_name;
            const p2 = match.participant2Name || match.participant_2_name;
            if (p1 && p2 && /Participant \d+/i.test(stored)) {
              const tn = match.tournamentName || match.tournament_name;
              return tn ? `${tn}: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
            }
            return stored;
          })()}</span>
        </div>

        {/* Participants + score */}
        <div className="list-participants">
          <span className="list-team">{p1Name}</span>
          <span className="list-score">{getScore('participant1')} – {getScore('participant2')}</span>
          <span className="list-team">{p2Name}</span>
        </div>

        {/* Sport */}
        <span className="list-meta">{match.sport_name || '—'}</span>

        {/* Scheduled */}
        <span className="list-meta">{formatDate(match.scheduledAt || match.scheduled_at, eventTimeZone(match))}</span>

        {/* Host */}
        <span className="list-meta">{hostName}</span>

        {/* Actions */}
        <div className="list-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="list-action-btn view"
            title="View"
            onClick={() => navigate(`/matches/${eid(match)}`, { state: { from: '/matches/manage', tab: filters.role } })}
          >👁️</button>
          {filters.role === 'organizer' && isCreator && (
            <>
              <button
                className="list-action-btn edit"
                title="Edit"
                onClick={() => navigate(`/matches/${eid(match)}`, { state: { from: '/matches/manage', tab: filters.role, openEdit: true } })}
              >✏️</button>
              {(match.status === 'scheduled' || match.status === 'created') && (
                <button
                  className="list-action-btn start"
                  title="Start"
                  onClick={() => navigate(
                    match.sport_name?.toLowerCase() === 'badminton'
                      ? `/matches/${match.id}/score`
                      : `/matches/${match.id}/live`,
                    { state: { from: '/matches/manage', tab: filters.role } }
                  )}
                >▶️</button>
              )}
              <button
                className="list-action-btn delete"
                title="Delete"
                onClick={() => { setMatchToDelete(match); setShowDeleteModal(true); }}
              >🗑️</button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <GameLayout>
        <div className="matches-gaming-layout">
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-1">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>⚽ Loading Your Matches</h3>
                <p>Fetching your match history...</p>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (error) {
    return (
      <GameLayout>
        <div className="matches-gaming-layout">
          <div className="error-gaming-floating">
            <FloatingCard variant="danger" size="large" className="delay-1">
              <div className="error-content-gaming">
                <div className="error-icon">❌</div>
                <h3>Error Loading Matches</h3>
                <p className="error-message">{error}</p>
                <div className="error-actions">
                  <FloatingButton
                    variant="secondary"
                    size="large"
                    onClick={() => navigate('/dashboard')}
                    icon="←"
                  >
                    <span className="btn-text">Back to Dashboard</span>
                  </FloatingButton>
                  <FloatingButton
                    variant="primary"
                    size="large"
                    onClick={loadMyMatches}
                    icon="🔄"
                  >
                    <span className="btn-text">Try Again</span>
                  </FloatingButton>
                </div>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
                  Debug: User: {JSON.stringify(currentUser)}, Token: {token ? 'Present' : 'Missing'}
                </p>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout>
      <div className="match-list-gaming">
        {/* Header */}
        <div className="matches-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="matches-title-gaming">
                  ⚽ Match Arena
                </h1>
                <p className="matches-subtitle-gaming">
                  Manage and track all your epic matches
                </p>
              </div>

              <div className="header-actions-gaming">
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => navigate('/dashboard')}
                  icon="←"
                >
                  <span className="btn-text">Back</span>
                </FloatingButton>
                <FloatingButton
                  variant="success"
                  size="large"
                  onClick={() => navigate("/matches/create")}
                  icon="⚔️"
                  className="delay-2"
                >
                  <span className="btn-text">Create Match</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Gaming Filters - Floating */}
        <div className="matches-filters-gaming">
          <FloatingCard variant="outline" size="medium" className="delay-3">
            <div className="filters-content-gaming">
              <h3 className="filters-title-gaming">🔍 Filter Matches</h3>

              {/* Role Toggle */}
              <div className="role-toggle-section">
                <div className="role-toggle-buttons">
                  <button
                    className={`role-toggle-btn ${filters.role === "participant" ? "active" : ""}`}
                    onClick={() => handleFilterChange("role", "participant")}
                  >
                    <span className="role-icon">🎮</span>
                    <span className="role-text">Playing In</span>
                  </button>
                  <button
                    className={`role-toggle-btn ${filters.role === "organizer" ? "active" : ""}`}
                    onClick={() => handleFilterChange("role", "organizer")}
                  >
                    <span className="role-icon">👑</span>
                    <span className="role-text">Organizing</span>
                  </button>
                  <button
                    className={`role-toggle-btn ${filters.role === "pending" ? "active" : ""}`}
                    onClick={() => handleFilterChange("role", "pending")}
                  >
                    <span className="role-icon">⚔️</span>
                    <span className="role-text">Pending Requests</span>
                  </button>
                </div>
              </div>

              {/* Search / Status / View filters — hidden on Pending tab */}
              {filters.role !== "pending" && (
              <div className="filters-grid-gaming">
                <div className="filter-group-gaming">
                  <label>🔎 Search</label>
                  <input
                    type="text"
                    placeholder="Search matches..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                    className="gaming-input"
                  />
                </div>

                <div className="filter-group-gaming">
                  <label>⚡ Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="gaming-select"
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">📅 Scheduled</option>
                    <option value="in_progress">🔴 In Progress</option>
                    <option value="completed">✅ Completed</option>
                    <option value="cancelled">❌ Cancelled</option>
                  </select>
                </div>

                <div className="filter-group-gaming">
                  <label>👁️ View</label>
                  <select
                    value={filters.viewMode}
                    onChange={(e) => handleFilterChange("viewMode", e.target.value)}
                    className="gaming-select"
                  >
                    <option value="grid">⊞ Grid View</option>
                    <option value="list">☰ List View</option>
                  </select>
                </div>

                <div className="filter-actions-gaming">
                  <FloatingButton
                    variant="warning"
                    size="small"
                    onClick={clearFilters}
                    icon="🧹"
                  >
                    <span className="btn-text">Clear</span>
                  </FloatingButton>

                  <FloatingButton
                    variant="info"
                    size="small"
                    onClick={loadMyMatches}
                    icon="🔄"
                  >
                    <span className="btn-text">Refresh</span>
                  </FloatingButton>
                </div>
              </div>
              )}
            </div>
          </FloatingCard>
        </div>

        {/* Scrollable content — cards / list / pending / empty */}
        <div
          ref={gridRef}
          className="matches-content-gaming"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop <= el.clientHeight + 150) {
              setVisibleCount(prev => Math.min(prev + 12, filteredMatches.length));
            }
          }}
        >
          {/* Pending Requests tab */}
          {filters.role === "pending" && (
            <PendingChallenges onChallengeResolved={loadMyMatches} />
          )}

          {/* Matches Grid/List */}
          {filters.role !== "pending" && (
            filteredMatches.length === 0 ? (
            <FloatingCard variant="outline" size="large" className="delay-5">
              <div className="empty-content-gaming">
                <div className="empty-icon">⚽</div>
                <h3>No Matches Found</h3>
                <p>
                  {matches.length === 0
                    ? "The arena awaits your first epic match!"
                    : "No matches match your current filters."}
                </p>
                <FloatingButton
                  variant="success"
                  size="large"
                  onClick={() => navigate("/matches/create")}
                  icon="⚔️"
                >
                  <span className="btn-text">Create Your First Match</span>
                </FloatingButton>
              </div>
            </FloatingCard>
            ) : (
            <div className={filters.viewMode === 'list' ? 'matches-grid-gaming list-view' : 'matches-grid-gaming cards-view'}>
              {filteredMatches.slice(0, visibleCount).map((match, index) =>
                filters.viewMode === 'list'
                  ? <MatchListRow key={match.id} match={match} index={index} />
                  : <MatchCard key={match.id} match={match} index={index} />
              )}
            </div>
            )
          )}

          {/* Scroll indicator */}
          {filters.role !== "pending" && filteredMatches.length > visibleCount && (
            <div style={{ textAlign: 'center', padding: '0.4rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
              {visibleCount} of {filteredMatches.length} — scroll for more
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay-gaming">
          <FloatingCard variant="danger" size="large" className="modal-card">
            <div className="modal-content-gaming">
              <div className="modal-header-gaming">
                <h3>🗑️ Delete Match</h3>
              </div>
              <div className="modal-body-gaming">
                <p>
                  Are you sure you want to delete the match{" "}
                  <strong>
                    {matchToDelete?.match_name || `Match #${matchToDelete?.id}`}
                  </strong>
                  ?
                </p>
                <p className="warning-text-gaming">
                  ⚠️ This action cannot be undone. All match data and events will be permanently removed.
                </p>
              </div>
              <div className="modal-actions-gaming">
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMatchToDelete(null);
                  }}
                  icon="❌"
                >
                  <span className="btn-text">Cancel</span>
                </FloatingButton>
                <FloatingButton
                  variant="danger"
                  size="medium"
                  onClick={handleDeleteMatch}
                  icon="🗑️"
                >
                  <span className="btn-text">Delete Match</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>
      )}
    </GameLayout>
  );
};

export default MatchManagement;